// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Formatting helpers — kept pure so they can be unit-tested.

export function formatNumber(n: number, decimals = 0): string {
  if (!Number.isFinite(n)) return '–';
  return n.toLocaleString('en-AU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

// kg → human-friendly weight string with appropriate unit.
export function formatMass(kg: number): string {
  if (!Number.isFinite(kg) || kg === 0) return '0 kg';
  const abs = Math.abs(kg);
  if (abs < 1) return `${formatNumber(kg, 3)} kg`;
  if (abs < 1000) return `${formatNumber(kg, abs < 10 ? 2 : 0)} kg`;
  const t = kg / 1000;
  if (abs < 1_000_000) return `${formatNumber(t, t < 10 ? 2 : t < 100 ? 1 : 0)} t`;
  const kt = kg / 1_000_000;
  if (abs < 1_000_000_000) return `${formatNumber(kt, kt < 10 ? 2 : 1)} kt`;
  return `${formatNumber(kg / 1_000_000_000, 2)} Mt`;
}

// Format a percent change like "+182.4%" or "-31.2%".
export function formatPct(p: number, decimals = 1): string {
  if (!Number.isFinite(p)) return '–';
  const s = p.toFixed(decimals);
  return p > 0 ? `+${s}%` : `${s}%`;
}

// 2024/2025 ↔ "2024–25" for compact display.
export function formatYear(y: number | null | undefined): string {
  if (y == null) return '—';
  const next = (y + 1) % 100;
  return `${y}–${String(next).padStart(2, '0')}`;
}

// Convert a 2024/2025-style report year string to its start year.
export function reportYearStart(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = /^(\d{4})/.exec(s);
  return m ? Number(m[1]) : null;
}

// Slugify a string for use in URL hashes.
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Tally air+water+land into a single total kg.
export interface Triple {
  a?: number;
  w?: number;
  l?: number;
  air?: number;
  water?: number;
  land?: number;
}
export function tripleTotal(t: Triple): number {
  return (t.a ?? t.air ?? 0) + (t.w ?? t.water ?? 0) + (t.l ?? t.land ?? 0);
}
